import { PropsWithChildren, useMemo } from 'react';

import Box, { BoxProps } from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import get from 'lodash/get';

export interface BadgeProps extends PropsWithChildren {
  type:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
    | 'orange'
    | 'none'
    | 'grey'
    | 'green';

  darken?: boolean;
  small?: boolean;
  sx?: BoxProps['sx'];
}

const Badge = styled(Box)`
  border-radius: 4px;
  padding: 3px 6px;
  white-space: nowrap;
`;

export const CustomBadge = ({
  type,
  children,
  darken,
  small,
  ...props
}: BadgeProps) => {
  const theme = useTheme();
  const colorsMapping: Record<BadgeProps['type'], string[]> = useMemo(() => {
    const colors = theme.colors;
    return {
      primary: [
        colors.primary.light,
        colors.primary.lighter,
        colors.primary.dark,
      ],
      secondary: [
        colors.secondary.light,
        colors.secondary.lighter,
        colors.secondary.dark,
      ],
      success: [
        colors.success.light,
        colors.success.lighter,
        colors.success.dark,
      ],
      error: [colors.error.border, colors.error.lighter, colors.error.dark],
      info: [colors.info.light, colors.info.lighter, colors.info.dark],
      warning: [
        colors.warning.light,
        colors.warning.lighter,
        colors.warning.dark,
      ],
      orange: [colors.orange, colors.orange, theme.background.white],
      green: [
        colors.success.light,
        colors.success.lighter,
        colors.success.text!,
      ],
      grey: [
        theme.custom!.badgeGrey,
        theme.custom!.badgeGrey,
        colors.secondary.text!,
      ],
      none: ['transparent', 'transparent', colors.primary.dark],
    };
  }, [theme]);

  const sx: BoxProps['sx'] = useMemo(() => {
    const [light, lighter, dark] = get(colorsMapping, [type]) ?? [];
    return {
      backgroundColor: darken ? light : lighter,
      borderRadius: '4px',
      color: dark,
      fontSize: small ? '12px' : '14px',
      lineHeight: small ? '14px' : '16px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'inline-flex',
      fontWeight: 500,
      alignItems: 'center',
      justifyContent: 'center',
      height: small ? '20px' : '24px',
      ...props.sx,
    };
  }, [colorsMapping, darken, props.sx, small, type]);
  return <Badge sx={sx}>{children}</Badge>;
};

export default CustomBadge;
